import * as Sentry from '@sentry/angular';
import { clientConfig } from '../config/client-config';
import { clientLogger } from '../logger/client.logger';

export interface InitializeSentryOptions {
    /** When set, used instead of resolving SENTRY_DSN from client config / window.__ENV__. */
    dsn?: string;
    /** When set, used instead of resolving NODE_ENV. */
    release?: string;
    /** Same-origin path (e.g. `/api/sentry-tunnel`) to proxy envelopes and avoid ad-blocker / CORS issues. */
    tunnel?: string;
}

let hasWarnedMissingDsn = false;

function ensureConfigInitialized(): void {
    clientConfig.define('BUILD_VERSION', false, {
        sourceKey: ['BUILD_VERSION', 'VITE_BUILD_VERSION'],
    });
    clientConfig.define('SENTRY_DSN', false, {
        sourceKey: ['SENTRY_DSN', 'VITE_SENTRY_DSN'],
    });
    clientConfig.define('SENTRY_DISABLED', false, {
        defaultValue: false,
        sourceKey: ['SENTRY_DISABLED', 'VITE_SENTRY_DISABLED'],
    });
    clientConfig.define('NODE_ENV', true, {
        defaultValue: 'development',
        sourceKey: ['NODE_ENV', 'VITE_NODE_ENV'],
    });
}

function isTruthyEnvFlag(value: unknown): boolean {
    if (typeof value === 'boolean') return value;
    return typeof value === 'string' && ['1', 'true', 'yes', 'on'].includes(value.trim().toLowerCase());
}

function isSentryDisabled(): boolean {
    return isTruthyEnvFlag(clientConfig.get('SENTRY_DISABLED'));
}

function resolveSentryRelease(options: InitializeSentryOptions): string {
    if (options.release) {
        return options.release;
    }

    return clientConfig.get('NODE_ENV') ?? 'development';
}

function warnMissingDsnOnce(): void {
    if (hasWarnedMissingDsn) return;
    hasWarnedMissingDsn = true;
    clientLogger.warn('[SENTRY] Skipping Sentry initialization - SENTRY_DSN not configured', {
        context: 'config',
        sourceKeys: ['VITE_SENTRY_DSN', 'window.__ENV__.SENTRY_DSN'],
    });
}

export function initializeSentryConfig(options: InitializeSentryOptions = {}): void {
    if (Sentry.getClient()) {
        return;
    }

    ensureConfigInitialized();

    if (isSentryDisabled()) {
        clientLogger.info('[SENTRY] Client-side Sentry disabled by SENTRY_DISABLED/VITE_SENTRY_DISABLED');
        return;
    }

    const sentryDsn = options.dsn ?? clientConfig.get('SENTRY_DSN');

    // Only initialize Sentry if DSN is provided
    if (!sentryDsn) {
        warnMissingDsnOnce();
        return;
    }

    const environment = clientConfig.get('NODE_ENV') ?? 'development';
    const release = resolveSentryRelease(options);

    Sentry.init({
        dsn: sentryDsn,
        environment,
        release,
        // Sentry 11: `sendDefaultPii` was replaced by granular `dataCollection`; logs are on by default.
        dataCollection: { userInfo: true },
        ...(options.tunnel ? { tunnel: options.tunnel } : {}),
        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration(),
            Sentry.feedbackIntegration({
                colorScheme: 'system',
            }),
        ],
        tracesSampleRate: 1.0,
        tracePropagationTargets: ['localhost', /^\/api/],
        replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
        replaysOnErrorSampleRate: 1.0,
    });

    clientLogger.info('[SENTRY] Client-side Sentry initialized successfully');
}

export function captureException(error: Error): void {
    if (isSentryDisabled() || !Sentry.getClient()) return;
    Sentry.captureException(error);
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info'): void {
    if (isSentryDisabled() || !Sentry.getClient()) return;
    Sentry.captureMessage(message, level);
}

export const sentryMetrics = Sentry.metrics;